/**
 * The help sidebar and the `i` badge (HELP_SYSTEM_SPEC §2; Design System Addenda §20).
 *
 * One `HelpProvider` per page layout owns which entry is open. `HelpSidebar` shows the page's
 * default entry (`<surface>.<screen>`), its related links and a search box; `HelpBadge` sits after a
 * key element's label, shows the entry's ≤120-character label on hover or focus, and opens the
 * sidebar on that entry when clicked. Content comes from packages/help and nowhere else, so the
 * badge, the sidebar and the `/help` documentation cannot disagree. A badge whose id has no entry
 * throws at render — the lint catches it before that, in CI.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode as RN } from "react";
import { getHelp, hasHelp, screenDefault, searchHelp, bodyHtml, type HelpEntry, type Surface, type Audience } from "@oc/help";
import { h, cx, type ReactNode } from "./h.ts";

export interface HelpApi {
  surface: Surface;
  screen: string;
  currentId: string;
  open: (id: string) => void;
  collapsed: boolean;
  toggle: () => void;
}

const HelpContext = createContext<HelpApi | null>(null);
const STORAGE_KEY = "oc.help.collapsed";

const readCollapsed = (surface: Surface): boolean => {
  try {
    const saved = globalThis.localStorage?.getItem(STORAGE_KEY);
    return saved === null || saved === undefined ? surface !== "console" : saved === "1";
  } catch { return surface !== "console"; }
};
const writeCollapsed = (v: boolean) => { try { globalThis.localStorage?.setItem(STORAGE_KEY, v ? "1" : "0"); } catch { /* a private window; the default stands */ } };

/** Mounted once per page. `?` toggles the sidebar unless the person is typing. */
export function HelpProvider({ surface, screen, children }: { surface: Surface; screen: string; children: RN }): ReactNode {
  const defaultId = `${surface}.${screen}`;
  const [currentId, setCurrent] = useState(defaultId);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(surface));
  useEffect(() => { setCurrent(defaultId); }, [defaultId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key === "?" && !(t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) setCollapsed((c) => { writeCollapsed(!c); return !c; });
    };
    globalThis.addEventListener?.("keydown", onKey);
    return () => globalThis.removeEventListener?.("keydown", onKey);
  }, []);
  const api = useMemo<HelpApi>(() => ({
    surface, screen, currentId,
    open: (id) => { getHelp(id); setCurrent(id); setCollapsed(false); writeCollapsed(false); },
    collapsed, toggle: () => setCollapsed((c) => { writeCollapsed(!c); return !c; }),
  }), [surface, screen, currentId, collapsed]);
  return h(HelpContext.Provider, { value: api }, children);
}

export const useHelp = (): HelpApi | null => useContext(HelpContext);

/** The `i` after a key element's label. Accessible name "Help: {title}". Never covers the element. */
export function HelpBadge({ id }: { id: string }): ReactNode {
  const api = useContext(HelpContext);
  if (api === null) return null;
  const entry = getHelp(id);
  return h("button", {
    type: "button", className: "oc-help-badge", "aria-label": `Help: ${entry.title}`, "data-help-id": id,
    onClick: (e: { stopPropagation: () => void }) => { e.stopPropagation(); api.open(id); },
  }, "i", h("span", { className: "lbl", role: "tooltip" }, entry.label));
}

export interface HelpSidebarProps {
  /** Which audiences' entries search may return — the console sees everything, a public page sees owner and broker. */
  audiences?: Audience[];
  /** Renders a related entry as a link to the documentation route instead of opening it in place. */
  docHref?: (entry: HelpEntry) => string;
  /** Internal review status is for operators, not public help readers. */
  showStatus?: boolean;
}

export function HelpSidebar({ audiences, docHref, showStatus = true }: HelpSidebarProps): ReactNode {
  const api = useContext(HelpContext);
  if (api === null) throw new Error("HelpSidebar needs a HelpProvider above it — every page layout mounts one");
  const entry = hasHelp(api.currentId) ? getHelp(api.currentId) : screenDefault(api.surface, api.screen);
  const isDefault = entry.id === `${api.surface}.${api.screen}`;
  const [query, setQuery] = useState("");
  const results = query.trim() === "" ? [] : searchHelp(query, 6, audiences);
  return h("aside", { className: cx("oc-help", api.collapsed && "collapsed"), "aria-label": "Help", "data-help-id": entry.id },
    h("div", { className: "hd" },
      h("span", { className: "eyebrow" }, isDefault ? "About this page" : "Help"),
      h("button", { type: "button", className: "btn-link oc-help-toggle", "aria-expanded": api.collapsed ? "false" : "true", onClick: api.toggle }, api.collapsed ? "Show help (?)" : "Hide (?)"),
    ),
    h("div", { className: "title" }, entry.title),
    h("div", { className: "body", dangerouslySetInnerHTML: { __html: bodyHtml(entry) } }),
    entry.related.length > 0 ? h("div", { className: "related" },
      h("span", { className: "oc-caption" }, "Related"),
      ...entry.related.map((rid) => {
        const r = getHelp(rid);
        return docHref
          ? h("a", { key: rid, href: docHref(r) }, r.title)
          : h("button", { key: rid, type: "button", className: "btn-link", onClick: () => api.open(rid) }, r.title);
      }),
      isDefault ? null : h("button", { type: "button", className: "btn-link", onClick: () => api.open(`${api.surface}.${api.screen}`) }, "Back to this page"),
    ) : null,
    h("div", { className: "search" },
      h("label", { className: "oc-field" }, h("span", { className: "oc-label" }, "Search help"),
        h("input", { type: "text", value: query, placeholder: "A word from the screen", onChange: (e: { target: { value: string } }) => setQuery(e.target.value) })),
      results.length > 0 ? h("div", { className: "results" }, ...results.map((r) => h("button", { key: r.id, type: "button", onClick: () => { api.open(r.id); setQuery(""); } }, r.title, h("span", null, r.label)))) : null,
    ),
    showStatus && entry.status === "PROPOSED" ? h("div", { className: "status" }, "Wording proposed — not yet accepted by the founder.") : null,
  );
}
